import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Building2, Shield, DollarSign, FileCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import CTAButtons from '../components/CTAButtons';
import RegistrationModal from '../components/RegistrationModal';

const CompanyFormation = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const benefits = [
    {
      icon: Shield,
      title: 'Limited Liability Protection',
      description: 'Protect your personal assets from business debts and legal liabilities. Your personal property stays safe.'
    },
    {
      icon: DollarSign,
      title: 'Tax Advantages',
      description: 'Choose how your business is taxed (LLC or Corporation) to maximize potential tax savings and benefits.'
    },
    {
      icon: FileCheck,
      title: 'Professional Credibility',
      description: 'Establish legitimacy and build trust with customers, suppliers, and potential investors.'
    },
    {
      icon: Building2,
      title: 'Business Flexibility',
      description: 'Easy to manage with flexible ownership structures and simplified administrative requirements.'
    }
  ];

  const entityTypes = [
    {
      name: 'Limited Liability Company (LLC)',
      description: 'The most popular choice for small businesses, offering liability protection with simple management.',
      features: [
        'Personal liability protection',
        'Pass-through taxation (no double taxation)',
        'Flexible management structure',
        'Fewer compliance requirements',
        'Can have single or multiple members'
      ]
    },
    {
      name: 'Corporation (C-Corp)',
      description: 'Best for businesses planning to raise capital or go public, with more complex structure.',
      features: [
        'Strongest liability protection',
        'Unlimited shareholders',
        'Easier to raise capital',
        'Better for attracting investors',
        'Potential for stock options'
      ]
    },
    {
      name: 'S Corporation (S-Corp)',
      description: 'Combines corporation benefits with pass-through taxation for eligible small businesses.',
      features: [
        'Pass-through taxation',
        'Liability protection',
        'Tax savings on self-employment',
        'Limited to 100 shareholders',
        'Must meet IRS requirements'
      ]
    }
  ];

  const process = [
    'Choose your business entity type (LLC, C-Corp, or S-Corp)',
    'Select and verify your company name availability',
    'Prepare and file Articles of Organization/Incorporation',
    'Obtain your Employer Identification Number (EIN)',
    'Create operating agreements or corporate bylaws',
    'Receive your official formation documents'
  ];

  return (
    <div>
      <Helmet>
        <title>Company Formation Services - LLC & Corporation Filing | Global Trademark Pro</title>
        <meta name="description" content="Start your business with professional company formation services. Form your LLC or Corporation with expert guidance, fast processing, and complete compliance support." />
        <meta name="keywords" content="company formation, LLC formation, incorporate business, start a company, business registration, corporate formation" />
        <link rel="canonical" href="https://yourdomain.com/company-formation" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
                Company Formation
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Start Your Business the Right Way
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Form your LLC or Corporation with confidence. We handle all the paperwork and compliance, so you can focus on building your business.
              </p>
              <CTAButtons buttonText="Start Company Formation" />
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1758599543129-5269a8f29e68"
                alt="Company formation"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Form a Legal Business Entity?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect yourself and maximize your business potential
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Entity Types */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Business Structure
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select the entity type that best fits your business goals
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {entityTypes.map((entity, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{entity.name}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{entity.description}</p>
                <div className="space-y-3">
                  {entity.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Our Simple 3-Step Process
            </h2>
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8 shadow-xl">
              <div className="space-y-6">
                {process.map((step, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Company Formation Packages
            </h2>
            <p className="text-xl text-gray-600">Choose the package that fits your business needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Basic Formation',
                price: 149,
                features: [
                  'Business name availability check',
                  'Preparation of formation documents',
                  'State filing of Articles',
                  'EIN application assistance',
                  'Digital copies of all documents',
                  'Email support'
                ]
              },
              {
                name: 'Standard Formation',
                price: 299,
                featured: true,
                features: [
                  'Everything in Basic, plus:',
                  'Operating Agreement/Bylaws template',
                  'Registered Agent service (1 year)',
                  'Company kit with documents',
                  'Banking resolution documents',
                  'Annual compliance reminders',
                  'Priority processing',
                  'Phone support'
                ]
              },
              {
                name: 'Premium Formation',
                price: 499,
                features: [
                  'Everything in Standard, plus:',
                  'Business website setup assistance',
                  'Business bank account recommendations',
                  'Registered Agent service (2 years)',
                  'Trademark name search',
                  'Business license research',
                  'S-Corp election assistance',
                  'Dedicated account manager',
                  '24/7 priority support'
                ]
              }
            ].map((pkg, index) => (
              <div key={index} className={`bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all ${pkg.featured ? 'border-2 border-emerald-500 scale-105' : 'border border-gray-200'}`}>
                {pkg.featured && (
                  <div className="text-center mb-4">
                    <span className="bg-emerald-600 text-white px-4 py-1 rounded-full text-xl font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-emerald-600">${pkg.price}</span>
                  <span className="text-gray-500 text-xl ml-2">+ State fees</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-xl">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => setIsModalOpen(true)} className={`w-full ${pkg.featured ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800' : 'bg-gray-900 hover:bg-gray-800'}`}>
                  Form My Company
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              What's Included in Our Service
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Complete name availability search',
                'Preparation of formation documents',
                'State filing of Articles of Organization/Incorporation',
                'Employer Identification Number (EIN) assistance',
                'Operating Agreement or Corporate Bylaws templates',
                'Registered agent service (first year included)',
                'Company kit with official documents',
                'Ongoing compliance support and guidance'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-gradient-to-br from-emerald-50 to-blue-50 p-6 rounded-xl shadow-lg">
                  <Check className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-emerald-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let us handle the paperwork while you focus on building your dream business.
          </p>
          <CTAButtons className="justify-center" buttonText="Start Company Formation" variant="dark" />
        </div>
      </section>
      <RegistrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
};

export default CompanyFormation;